// react
import React from "react";
// redux
import { useDispatch, useSelector } from "react-redux";
// entities
import { getFilterAppsState, filterAppsActions } from "@/entities/filterApps";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { counterActions } from "@/entities/counter";

// hooks
import useHandleClickAppCollection from "../../libs/hook/useHandleClickAppCollection";
// types
import { IAppCollectionsModalProps } from "../../libs/types/IAppCollectionsModalProps";
import { AppCollection } from "@/features/auth/types/auth";
// styles
import styles from "./AppCollectionsModal.module.scss";
import StoredCount from "@/shared/ui/storedcount/StoredCound";
import { handleFocus } from "@/widgets/inputSearch/libs/const/handleFocus";

import { ACTIVE_VIEW_HOME } from "@/Views/constants";
import AppCollectionsModalItem from "@/shared/ui/AppCollectionsModalItem/AppCollectionsModalItem";

const AppCollectionsModal: React.FC<IAppCollectionsModalProps> = ({
  appDataCollections,
  onSave,
  isFetchingFilter,
  dataAppCollections,
  setDataAppCollections,
  selectedAppCollectionIds,
  setSelectedAppCollectionIds,
  rootActiveView,
  hiddenCount,
}) => {
  console.log(appDataCollections);
  // selectors
  const { activeModalIndexAppCollections, only } =
    useSelector(getFilterAppsState);
  // custom hook
  const { handleItemClick, handleReset } = useHandleClickAppCollection(
    appDataCollections,
    onSave,
    isFetchingFilter,
    dataAppCollections,
    setDataAppCollections,
    selectedAppCollectionIds,
    setSelectedAppCollectionIds,
  );
  const dispatch = useDispatch();

  const [isResetVisible, setIsResetVisible] = React.useState(false);

  // Create mocked "Your Hidden Apps" collection
  const hiddenAppsCollection: AppCollection = {
    id: -1, // Use negative ID to distinguish from real collections
    name: "Your Hidden Apps",
    total_apps: hiddenCount || 0, // This would ideally come from hidden apps count
    apps_last8: [],
  };

  // Check if hidden apps collection is active
  const isHiddenAppsActive = only === "hidden";

  // Handle click for hidden apps collection
  const handleHiddenAppsClick = () => {
    if (!isFetchingFilter) {
      if (isHiddenAppsActive) {
        // Deactivate hidden apps filter
        dispatch(filterAppsActions.setOnly("not_hidden"));
        dispatch(counterActions.removeFromArray("Hidden"));
      } else {
        // Activate hidden apps filter
        dispatch(filterAppsActions.setOnly("hidden"));
        dispatch(counterActions.addToArray("Hidden"));
      }

      // Reset data to trigger refetch
      dispatch(filterAppsDataActions.resetFilterAppDataSorted());
      dispatch(filterAppsDataActions.resetFilterAppDataSortedAll());
      dispatch(filterAppsDataActions.resetFilterAppData());
      dispatch(filterAppsActions.resetPageFilter());
      dispatch(filterAppsActions.resetPageFilterSidebar());
    }
  };

  React.useEffect(() => {
    // Include hidden apps in reset visibility check
    setIsResetVisible(
      activeModalIndexAppCollections.length > 0 || isHiddenAppsActive,
    );
  }, [activeModalIndexAppCollections, isHiddenAppsActive]);

  // Enhanced reset function to handle hidden apps
  const handleEnhancedReset = () => {
    // Reset regular collections
    handleReset();

    // Reset hidden apps if active
    if (isHiddenAppsActive && !isFetchingFilter) {
      dispatch(filterAppsActions.setOnly("not_hidden"));
      dispatch(counterActions.removeFromArray("Hidden"));
      dispatch(filterAppsDataActions.resetFilterAppDataSorted());
      dispatch(filterAppsDataActions.resetFilterAppDataSortedAll());
      dispatch(filterAppsDataActions.resetFilterAppData());
      dispatch(filterAppsActions.resetPageFilter());
      dispatch(filterAppsActions.resetPageFilterSidebar());
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.itemContainer}>
        <div className={styles.regularAppsContainer}>
          {/* Render regular collections */}
          {appDataCollections?.results.map((item, index) => {
            const isActive = activeModalIndexAppCollections.includes(index);
            return (
              <AppCollectionsModalItem
                key={item.id}
                item={item}
                onItemClick={() => handleItemClick(index)}
                isActive={isActive}
              />
            );
          })}
        </div>

        <div className={styles.hiddenAppsContainer}>
          <div className={styles.divider} />
          <div className={styles.hiddenAppsItem}>
            <AppCollectionsModalItem
              key={hiddenAppsCollection.id}
              item={hiddenAppsCollection}
              onItemClick={handleHiddenAppsClick}
              isActive={isHiddenAppsActive}
            />
          </div>
        </div>
      </div>

      {rootActiveView === ACTIVE_VIEW_HOME && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <StoredCount onFocus={() => handleFocus(dispatch)} />
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        {isResetVisible && (
          <div onClick={handleEnhancedReset} className={styles.reset}>
            <img src="/img/blackIcon/Close.svg" alt="resetImg" loading="lazy" />
            <p>Reset App Collections</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppCollectionsModal;
